import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	ClickAwayListener,
	Grid,
	Grow,
	ListItemText,
	MenuItem,
	MenuList,
	Paper,
	Popper,
	Stack,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRef, useState } from 'react';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import useModal from '@/common/components/ConfirmationDialog/useModal';

import { useNavigate, useParams } from 'react-router-dom';
import {
	DISPLAY_DATE_FORMAT,
	DISPLAY_DATE_TIME_FORMAT,
} from '@/utils/dateHelper';
import dayjs from 'dayjs';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
	useDeleteAdmissionRequestMutation,
	useGetAdmissionRequestDetailQuery,
} from '@/api/member-management/admissionRequest';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import { showLabel } from '@/utils/helper';
import {
	ISCASE_LIST,
	LANGUAGE_LIST,
	PREFIX_LIST,
	YESNOLIST,
} from '@/common/constants/common';
import { GridColDef } from '@mui/x-data-grid';
import { useGetAllEnumListQuery } from '@/api/common';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import HistorySummaryPage from '../change-history/HistorySummaryPage';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import NewProgramMemberDialog from './components/NewProgramMemberDialog';
import NewCaseMemberDialog from './components/NewCaseMemberDialog';
import FamilyCarersInfoHint from './components/FamilyCarersInfoHint';
import EstimatedAgeInfoHint from './components/EstimatedAgeInfoHint';
import { formatAge } from '@/utils/ageHelper';
import NewOpenCaseDialog from './components/NewOpenCaseDialog';
import NewCloseCaseDialog from './components/NewCloseCaseDialog';
import NewCloseAllProgramDialog from './components/NewCloseAllProgramDialog';
import NewCloseAllCaseDialog from './components/NewCloseAllCaseDialog';
import ClosedMemberPickerDialog from '@/common/components/member-duplication/ClosedMemberPickerDialog';
import {
	useLinkClosedMemberMutation,
	useLinkLegacyFileNoMutation,
	useUnlinkClosedMemberMutation,
} from '@/api/member-management/memberDuplication';
import FileNoHistoryButton from '@/common/components/member-duplication/FileNoHistoryButton';
import LinkedRecordBanner from '@/common/components/identity-link/LinkedRecordBanner';
import {
	MemberStatusEnum,
	PermissionEnum,
	ServiceFollowEnum,
} from '@/api/enum/common.enum';
import SubmitToICDialog from './components/SubmitToICDialog';
import ReturnFromICDialog from './components/ReturnFromICDialog';
import AssignCaseWorkersDialog from './components/AssignCaseWorkersDialog';
import AssignProgramWorkersDialog from './components/AssignProgramWorkersDialog';
import CloseNotSuitableDialog from './components/CloseNotSuitableDialog';
import TransferCaseWorkersDialog from './components/TransferCaseWorkersDialog';
import UpdateSharedWithProfessionalsDialog from './components/UpdateSharedWithProfessionalsDialog';
import TransferProgramWorkersDialog from './components/TransferProgramWorkersDialog';
import ClearAllCaseWorkersDialog from './components/ClearAllCaseWorkersDialog';
import ClearAllProgramWorkersDialog from './components/ClearAllProgramWorkersDialog';
import MemberInterventionSummaryPage from '../member-intervention/MemberInterventionSummaryPage';
import EditCalendarOutlinedIcon from '@mui/icons-material/EditCalendarOutlined';
import FamilyPage from './components/FamilyPage';
import { useGetUser } from '@/hooks/useGetUser';
import { useTabParam } from '@/hooks/useTabParam';
import ProgramAppLoginTab, {
	PROGRAM_APP_LOGIN_TAB,
	ProgramAppLoginTabLabel,
} from '@/common/components/program-app-login/ProgramAppLoginTab';

interface Props {
	type?: string;
}

export default function AdmissionRequestDetail(props: Props) {
	const { type } = props;
	const { t } = useTranslation();
	const [serviceDetailsExpanded, setServiceDetailsExpanded] = useState(true);
	const [personalInformationExpanded, setPersonalInformationExpanded] =
		useState(true);
	const [contactInformationExpanded, setContactInformationExpanded] =
		useState(true);
	const [familyCarersInformationExpanded, setFamilyCarersInformationExpanded] =
		useState(true);
	const [educationWorkLiveExpanded, setEducationWorkLiveExpanded] =
		useState(true);
	const [memberInformationExpanded, setMemberInformationExpanded] =
		useState(true);
	const [caseStatusExpanded, setCaseStatusExpanded] = useState(true);
	const [referrerInformationExpanded, setReferrerInformationExpanded] =
		useState(true);
	const [shareCaseServiceExpanded, setShareCaseServiceExpanded] =
		useState(true);
	const [mentalHealthInformationExpanded, setMentalHealthInformationExpanded] =
		useState(true);
	const anchorRef = useRef<HTMLDivElement>(null);
	const anchorRef2 = useRef<HTMLDivElement>(null);
	const [membershipButton, setMembershipButton] = useState(false);
	const [otherMemberButton, setOtherMemberButton] = useState(false);
	// Action dialogs are rendered at page level (below) and opened from the menus
	// via these keys, instead of being nested inside the open MenuList. This keeps
	// dialog keystrokes from bubbling into the MenuList's type-ahead handler.
	const [membershipDialog, setMembershipDialog] = useState<string | null>(null);
	const [otherDialog, setOtherDialog] = useState<string | null>(null);
	// Duplicate-check dialogs own an internal open/warning state machine, so they
	// stay mounted and are (re)triggered by bumping a per-key counter.
	const [dupTrigger, setDupTrigger] = useState<Record<string, number>>({});
	const openMembershipDialog = (key: string) => {
		setMembershipButton(false);
		setMembershipDialog(key);
	};
	const openOtherDialog = (key: string) => {
		setOtherMemberButton(false);
		setOtherDialog(key);
	};
	const fireDupTrigger = (key: string) => {
		setMembershipButton(false);
		setDupTrigger((prev) => ({ ...prev, [key]: (prev[key] || 0) + 1 }));
	};
	const { userInfo } = useGetUser();

	const [currentTab, setCurrentTab] = useTabParam('details');

	const { data: allEnumList } = useGetAllEnumListQuery(null, {
		refetchOnMountOrArgChange: true,
	});

	const { admissionRequestId } = useParams();

	const navigate = useNavigate();

	const columns1: GridColDef[] = [
		{
			field: 'clientName',
			headerName: t('preAdmission.clientName'),
			flex: 25,
		},
		{
			field: 'organization',
			headerName: t('preAdmission.organisation'),
			flex: 25,
		},
		{
			field: 'name',
			headerName: t('preAdmission.name'),
			flex: 25,
		},
		{
			field: 'telNo',
			headerName: t('preAdmission.telNo'),
			flex: 25,
		},
	];

	const showModal = useModal();
	const [deleteAdmissionRequest] = useDeleteAdmissionRequestMutation();
	const [linkClosedMember] = useLinkClosedMemberMutation();
	const [linkLegacyFileNo] = useLinkLegacyFileNoMutation();
	const [unlinkClosedMember] = useUnlinkClosedMemberMutation();

	const { data: details } = useGetAdmissionRequestDetailQuery(
		admissionRequestId || '',
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const handleDelete = async () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('member.deleteConfirmation')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{type == 'member'
							? t('member.deleteMemberTips')
							: t('member.deleteTips')}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.confirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteAdmissionRequest(
						admissionRequestId || ''
					);
					if ('data' in response) {
						enqueueSnackbar(
							type == 'member'
								? t('snackbar.deleteMemberSuccess', {
										fileNo: details?.data.fileNo,
									})
								: t('snackbar.deleteAdmissionRequestSuccess', {
										preAdmissionNo: details?.data.preAdmissionNo,
									}),
							{
								variant: 'warning',
								anchorOrigin: { horizontal: 'center', vertical: 'top' },
								autoHideDuration: 3000,
								action: (key) => {
									return <CloseIcon onClick={() => closeSnackbar(key)} />;
								},
							}
						);

						if (type == 'member') {
							navigate(`/member-management/member-list`, {
								replace: true,
							});
						} else {
							navigate(`/member-management/admission-request-list`, {
								replace: true,
							});
						}
					}
				}}
			/>
		));
	};

	// Visibility of each action menu item. Reused to decide whether the menu
	// item renders AND whether its parent action button is shown at all (an
	// empty menu must not display its trigger button).
	const memberStatus = details?.data.status;
	const serviceFollow = details?.data.serviceFollow;
	const has = (perm: PermissionEnum) =>
		!!userInfo?.permissionList?.includes(perm);

	// Every tab but Details is permission-gated, and the active tab comes from `?tab=`
	// — so a link (a notification jump, a bookmark, the back button) can name a tab
	// this staff member is not allowed to see. Fall back to Details in that case,
	// rather than rendering the hidden tab's content past its gate and leaving the
	// Tabs bar with a value none of its Tab children matches.
	const canSeeTab: Record<string, boolean> = {
		details: true,
		intervention: has(PermissionEnum.MemberInterventionSummaryTab),
		family: has(PermissionEnum.MemberManageFamilyMemberTab) && type == 'member',
		// Only an in-progress member has a Program App login account; not an
		// admission request, and not once all services are closed.
		[PROGRAM_APP_LOGIN_TAB]:
			has(PermissionEnum.ViewAdmissionRequestAndMember) &&
			type == 'member' &&
			memberStatus !== undefined &&
			[
				MemberStatusEnum.InProgressAdmitted,
				MemberStatusEnum.AwaitingAssignmentAdmitted,
				MemberStatusEnum.PendingIntakeAdmitted,
				MemberStatusEnum.IntakeCompletedAdmitted,
			].includes(memberStatus),
		history: has(PermissionEnum.MemberChangeHistoryTab),
	};
	const activeTab = canSeeTab[currentTab] ? currentTab : 'details';

	const canNewProgramMember =
		has(PermissionEnum.ChangeMembership_NewRegisterAsProgramMember) &&
		memberStatus !== undefined &&
		[
			MemberStatusEnum.InProgressNoAdmitted,
			MemberStatusEnum.AwaitingAssignmentNoAdmitted,
			MemberStatusEnum.PendingIntakeNoAdmitted,
			MemberStatusEnum.IntakeCompletedNoAdmitted,
		].includes(memberStatus);
	const canNewCaseMember =
		has(PermissionEnum.ChangeMembership_NewRegisterAsCaseMember) &&
		memberStatus !== undefined &&
		[
			MemberStatusEnum.InProgressNoAdmitted,
			MemberStatusEnum.AwaitingAssignmentNoAdmitted,
			MemberStatusEnum.PendingIntakeNoAdmitted,
			MemberStatusEnum.IntakeCompletedNoAdmitted,
		].includes(memberStatus);
	const canOpenCase =
		has(PermissionEnum.ChangeMembership_OpenCase) &&
		memberStatus !== undefined &&
		serviceFollow !== undefined &&
		[
			MemberStatusEnum.PendingIntakeAdmitted,
			MemberStatusEnum.IntakeCompletedAdmitted,
		].includes(memberStatus) &&
		[ServiceFollowEnum.Program, ServiceFollowEnum.ProgramCloseCase].includes(
			serviceFollow
		);
	const canCloseCase =
		has(PermissionEnum.ChangeMembership_CloseCase) &&
		memberStatus !== undefined &&
		serviceFollow !== undefined &&
		[
			MemberStatusEnum.PendingIntakeAdmitted,
			MemberStatusEnum.IntakeCompletedAdmitted,
		].includes(memberStatus) &&
		[ServiceFollowEnum.Case].includes(serviceFollow);
	const canCloseAllProgram =
		has(PermissionEnum.ChangeMembership_CloseAllMembershipWhenCaseNo) &&
		memberStatus !== undefined &&
		serviceFollow !== undefined &&
		[
			MemberStatusEnum.InProgressAdmitted,
			MemberStatusEnum.AwaitingAssignmentAdmitted,
			MemberStatusEnum.PendingIntakeAdmitted,
			MemberStatusEnum.IntakeCompletedAdmitted,
		].includes(memberStatus) &&
		[ServiceFollowEnum.Program, ServiceFollowEnum.ProgramCloseCase].includes(
			serviceFollow
		);
	const canCloseAllCase =
		has(PermissionEnum.ChangeMembership_CloseAllMembershipWhenCaseYes) &&
		memberStatus !== undefined &&
		serviceFollow !== undefined &&
		[
			MemberStatusEnum.InProgressAdmitted,
			MemberStatusEnum.AwaitingAssignmentAdmitted,
			MemberStatusEnum.PendingIntakeAdmitted,
			MemberStatusEnum.IntakeCompletedAdmitted,
		].includes(memberStatus) &&
		[ServiceFollowEnum.Case].includes(serviceFollow);

	const hasMembershipActions =
		canNewProgramMember ||
		canNewCaseMember ||
		canOpenCase ||
		canCloseCase ||
		canCloseAllProgram ||
		canCloseAllCase;
	// CHA-001 §5.2: a record linked to a service-closed member registers as "Re-register".
	const isLinkedToClosedMember = !!(
		details?.data.linkedClosedUserId || details?.data.linkedLegacyFileNo
	);

	const canSubmitToIC =
		has(PermissionEnum.ActionButton_SubmitToIC) &&
		memberStatus !== undefined &&
		[
			MemberStatusEnum.InProgressNoAdmitted,
			MemberStatusEnum.InProgressAdmitted,
		].includes(memberStatus);
	const canReturnFromIC =
		has(PermissionEnum.ActionButton_ReturnFromIC) &&
		memberStatus !== undefined &&
		[
			MemberStatusEnum.AwaitingAssignmentNoAdmitted,
			MemberStatusEnum.AwaitingAssignmentAdmitted,
		].includes(memberStatus);
	const canAssignCaseWorkers =
		has(PermissionEnum.ActionButton_AssignCaseWorkers) &&
		memberStatus !== undefined &&
		[
			MemberStatusEnum.AwaitingAssignmentNoAdmitted,
			MemberStatusEnum.AwaitingAssignmentAdmitted,
		].includes(memberStatus);
	const canAssignProgramWorkers =
		has(PermissionEnum.ActionButton_AssignProgramWorkers) &&
		memberStatus !== undefined &&
		[
			MemberStatusEnum.AwaitingAssignmentNoAdmitted,
			MemberStatusEnum.AwaitingAssignmentAdmitted,
		].includes(memberStatus);
	const canCloseNotSuitable =
		has(PermissionEnum.ActionButton_CloseNotSuitable) &&
		memberStatus !== undefined &&
		[
			MemberStatusEnum.InProgressNoAdmitted,
			MemberStatusEnum.InProgressAdmitted,
			MemberStatusEnum.AwaitingAssignmentNoAdmitted,
			MemberStatusEnum.AwaitingAssignmentAdmitted,
			MemberStatusEnum.PendingIntakeNoAdmitted,
			MemberStatusEnum.PendingIntakeAdmitted,
			MemberStatusEnum.IntakeCompletedNoAdmitted,
			MemberStatusEnum.IntakeCompletedAdmitted,
		].includes(memberStatus);
	const canTransferCaseWorkers =
		has(PermissionEnum.ActionButton_TransferCaseWorkers) &&
		memberStatus !== undefined &&
		[
			MemberStatusEnum.PendingIntakeNoAdmitted,
			MemberStatusEnum.PendingIntakeAdmitted,
			MemberStatusEnum.IntakeCompletedNoAdmitted,
			MemberStatusEnum.IntakeCompletedAdmitted,
		].includes(memberStatus) &&
		details?.data.isCase === true;
	const canTransferProgramWorkers =
		has(PermissionEnum.ActionButton_TransferProgramWorkers) &&
		memberStatus !== undefined &&
		[
			MemberStatusEnum.PendingIntakeNoAdmitted,
			MemberStatusEnum.PendingIntakeAdmitted,
			MemberStatusEnum.IntakeCompletedNoAdmitted,
			MemberStatusEnum.IntakeCompletedAdmitted,
		].includes(memberStatus) &&
		details?.data.isCase === false;
	const canUpdateSharedWithProfessionals =
		has(PermissionEnum.ActionButton_UpdateSharedWithProfessionals) &&
		memberStatus !== undefined &&
		[
			MemberStatusEnum.PendingIntakeNoAdmitted,
			MemberStatusEnum.PendingIntakeAdmitted,
			MemberStatusEnum.IntakeCompletedNoAdmitted,
			MemberStatusEnum.IntakeCompletedAdmitted,
		].includes(memberStatus) &&
		!!details?.data.isCase;
	const canClearAllCaseWorkers =
		has(PermissionEnum.ActionButton_ClearAllCaseWorkers) &&
		memberStatus !== undefined &&
		[MemberStatusEnum.NotCounted, MemberStatusEnum.All].includes(
			memberStatus
		) &&
		!!details?.data.responsibleCaseWorkerId;
	const canClearAllProgramWorkers =
		has(PermissionEnum.ActionButton_ClearAllProgramWorkers) &&
		memberStatus !== undefined &&
		[MemberStatusEnum.NotCounted, MemberStatusEnum.All].includes(
			memberStatus
		) &&
		!!(
			details?.data.responsibleProgramWorkerId ||
			details?.data.responsibleProfessionalStaffId
		);
	// CHA-001 §5.1: Admission Request before admission only, same permission as
	// Submit to IC. Link while not linked; Unlink in the same place once linked.
	const canChangeClosedMemberLink =
		has(PermissionEnum.ActionButton_SubmitToIC) &&
		type != 'member' &&
		memberStatus !== undefined &&
		[
			MemberStatusEnum.InProgressNoAdmitted,
			MemberStatusEnum.AwaitingAssignmentNoAdmitted,
			MemberStatusEnum.PendingIntakeNoAdmitted,
			MemberStatusEnum.IntakeCompletedNoAdmitted,
		].includes(memberStatus);
	const canLinkClosedMember =
		canChangeClosedMemberLink && !isLinkedToClosedMember;
	const canUnlinkClosedMember =
		canChangeClosedMemberLink && isLinkedToClosedMember;

	const handleUnlinkClosedMember = () => {
		const fileNo =
			details?.data.linkedClosedUserFileNo || details?.data.linkedLegacyFileNo;
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('memberDuplication.unlinkConfirmTitle')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('memberDuplication.unlinkConfirmText', { fileNo })}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.confirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await unlinkClosedMember(admissionRequestId || '');
					if ('data' in response) {
						enqueueSnackbar(
							t('snackbar.unlinkClosedMemberSuccess', { fileNo }),
							{
								variant: 'success',
								anchorOrigin: { horizontal: 'center', vertical: 'top' },
								autoHideDuration: 3000,
								action: (key) => (
									<CloseIcon onClick={() => closeSnackbar(key)} />
								),
							}
						);
					}
				}}
			/>
		));
	};

	const hasOtherMemberActions =
		canSubmitToIC ||
		canReturnFromIC ||
		canAssignCaseWorkers ||
		canAssignProgramWorkers ||
		canCloseNotSuitable ||
		canTransferCaseWorkers ||
		canTransferProgramWorkers ||
		canUpdateSharedWithProfessionals ||
		canClearAllCaseWorkers ||
		canClearAllProgramWorkers ||
		canLinkClosedMember ||
		canUnlinkClosedMember;

	const columns: GridColDef[] = [
		{
			field: 'familyCarerName',
			headerName: t('preAdmission.name'),
			flex: 25,
		},
		{
			field: 'age',
			headerName: t('preAdmission.age'),
			flex: 25,
			renderCell: (params) => params.row.age || '-',
		},
		{
			field: 'relationshipDesc',
			headerName: t('preAdmission.relationship'),
			flex: 25,
		},
		{
			field: 'address',
			headerName: t('preAdmission.address'),
			flex: 25,
		},
		{
			field: 'telNo',
			headerName: t('preAdmission.telNo'),
			flex: 25,
		},
		{
			field: 'emergencyContact',
			headerName: t('preAdmission.emergencyContact'),
			flex: 25,
			renderCell: (params: any) =>
				showLabel(params.row.emergencyContact, YESNOLIST),
		},
	];

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Stack
					direction={'row'}
					alignItems={'center'}
					justifyContent={'space-between'}
					sx={{ marginBottom: '12px', marginTop: '6px' }}
				>
					<Typography variant='h4'>
						{type == 'member'
							? t('drawerListItems.memberDetails')
							: t('drawerListItems.admissionRequestDetails')}
					</Typography>

					<Stack direction={'row'} alignItems={'center'}>
						{has(PermissionEnum.SummaryOfForm) && (
							<Button
								endIcon={<AssignmentOutlinedIcon />}
								onClick={() => {
									navigate(
										`/member-management/${
											type == 'member'
												? 'member-list'
												: 'admission-request-list'
										}/detail/${admissionRequestId}/forms-management`
									);
								}}
								variant='contained'
								sx={{ marginRight: '20px' }}
							>
								{t('button.manageForms')}
							</Button>
						)}
						{hasMembershipActions && (
							<Box ref={anchorRef}>
								<Button
									onClick={() => {
										setOtherMemberButton(false);
										setMembershipButton((prevOpen) => !prevOpen);
									}}
									endIcon={<MoreVertOutlinedIcon />}
									variant='contained'
									sx={{ marginRight: '20px' }}
								>
									{t('button.membershipAction')}
								</Button>
							</Box>
						)}
						{hasOtherMemberActions && (
							<Box ref={anchorRef2}>
								<Button
									onClick={() => {
										setMembershipButton(false);
										setOtherMemberButton((prevOpen) => !prevOpen);
									}}
									endIcon={<MoreVertOutlinedIcon />}
									variant='contained'
									sx={{ marginRight: '20px' }}
								>
									{t('button.otherMemberAction')}
								</Button>
							</Box>
						)}
						{userInfo?.permissionList?.includes(
							PermissionEnum.DeleteAdmissionRequestAndMember
						) && (
							<Button
								endIcon={<DeleteOutlined />}
								onClick={() => handleDelete()}
								variant='contained'
							>
								{t('button.delete')}
							</Button>
						)}

						<Popper
							sx={{ zIndex: 1 }}
							open={membershipButton}
							anchorEl={anchorRef.current}
							role={undefined}
							transition
							disablePortal
						>
							{({ TransitionProps, placement }) => (
								<Grow
									{...TransitionProps}
									style={{
										transformOrigin:
											placement === 'bottom' ? 'center top' : 'center bottom',
									}}
								>
									<Paper>
										<MenuList id='split-button-menu' autoFocusItem>
											{canNewProgramMember ? (
												<MenuItem
													onClick={() => fireDupTrigger('newProgramMember')}
												>
													<ListItemText>
														{isLinkedToClosedMember
															? t('button.reRegisterProgram')
															: t('button.newProgramMember')}
													</ListItemText>
												</MenuItem>
											) : (
												''
											)}
											{canNewCaseMember ? (
												<MenuItem
													onClick={() => fireDupTrigger('newCaseMember')}
												>
													<ListItemText>
														{isLinkedToClosedMember
															? t('button.reRegisterCase')
															: t('button.newCaseMember')}
													</ListItemText>
												</MenuItem>
											) : (
												''
											)}
											{canOpenCase ? (
												<MenuItem
													onClick={() => openMembershipDialog('openCase')}
												>
													<ListItemText>{t('button.openCase')}</ListItemText>
												</MenuItem>
											) : (
												''
											)}
											{canCloseCase ? (
												<MenuItem
													onClick={() => openMembershipDialog('closeCase')}
												>
													<ListItemText>{t('button.closeCase')}</ListItemText>
												</MenuItem>
											) : (
												''
											)}
											{canCloseAllProgram ? (
												<MenuItem
													onClick={() =>
														openMembershipDialog('closeAllProgram')
													}
												>
													<ListItemText>
														{t('button.closeAllProgram')}
													</ListItemText>
												</MenuItem>
											) : (
												''
											)}
											{canCloseAllCase ? (
												<MenuItem
													onClick={() => openMembershipDialog('closeAllCase')}
												>
													<ListItemText>
														{t('button.closeAllCase')}
													</ListItemText>
												</MenuItem>
											) : (
												''
											)}
										</MenuList>
									</Paper>
								</Grow>
							)}
						</Popper>
						<Popper
							sx={{ zIndex: 1 }}
							open={otherMemberButton}
							anchorEl={anchorRef2.current}
							role={undefined}
							transition
							disablePortal
						>
							{({ TransitionProps, placement }) => (
								<Grow
									{...TransitionProps}
									style={{
										transformOrigin:
											placement === 'bottom' ? 'center top' : 'center bottom',
									}}
								>
									<Paper>
										<MenuList id='split-button-menu' autoFocusItem>
											{canSubmitToIC ? (
												<MenuItem onClick={() => openOtherDialog('submitToIC')}>
													<ListItemText>{t('button.submitToIC')}</ListItemText>
												</MenuItem>
											) : (
												''
											)}
											{canReturnFromIC ? (
												<MenuItem
													onClick={() => openOtherDialog('returnFromIC')}
												>
													<ListItemText>
														{t('button.returnFromIC')}
													</ListItemText>
												</MenuItem>
											) : (
												''
											)}
											{canAssignCaseWorkers ? (
												<MenuItem
													onClick={() => openOtherDialog('assignCaseWorkers')}
												>
													<ListItemText>
														{t('button.assignCaseWorkers')}
													</ListItemText>
												</MenuItem>
											) : (
												''
											)}
											{canAssignProgramWorkers ? (
												<MenuItem
													onClick={() =>
														openOtherDialog('assignProgramWorkers')
													}
												>
													<ListItemText>
														{t('button.assignProgramWorkers')}
													</ListItemText>
												</MenuItem>
											) : (
												''
											)}
											{canCloseNotSuitable ? (
												<MenuItem
													onClick={() => openOtherDialog('closeNotSuitable')}
												>
													<ListItemText>
														{t('button.closeNotSuitable')}
													</ListItemText>
												</MenuItem>
											) : (
												''
											)}
											{canTransferCaseWorkers ? (
												<MenuItem
													onClick={() => openOtherDialog('transferCaseWorkers')}
												>
													<ListItemText>
														{t('button.transferCaseWorkers')}
													</ListItemText>
												</MenuItem>
											) : (
												''
											)}
											{canTransferProgramWorkers ? (
												<MenuItem
													onClick={() =>
														openOtherDialog('transferProgramWorkers')
													}
												>
													<ListItemText>
														{t('button.transferProgramWorkers')}
													</ListItemText>
												</MenuItem>
											) : (
												''
											)}
											{canUpdateSharedWithProfessionals ? (
												<MenuItem
													onClick={() =>
														openOtherDialog('updateSharedWithProfessionals')
													}
												>
													<ListItemText>
														{t('button.updateSharedWithProfessionals')}
													</ListItemText>
												</MenuItem>
											) : (
												''
											)}
											{canClearAllCaseWorkers ? (
												<MenuItem
													onClick={() => openOtherDialog('clearAllCaseWorkers')}
												>
													<ListItemText>
														{t('button.clearAllCaseWorkers')}
													</ListItemText>
												</MenuItem>
											) : (
												''
											)}
											{canClearAllProgramWorkers ? (
												<MenuItem
													onClick={() =>
														openOtherDialog('clearAllProgramWorkers')
													}
												>
													<ListItemText>
														{t('button.clearAllProgramWorkers')}
													</ListItemText>
												</MenuItem>
											) : (
												''
											)}
											{canLinkClosedMember ? (
												<MenuItem
													onClick={() => openOtherDialog('linkClosedMember')}
												>
													<ListItemText>
														{t('button.linkClosedMember')}
													</ListItemText>
												</MenuItem>
											) : (
												''
											)}
											{canUnlinkClosedMember ? (
												<MenuItem
													onClick={() => {
														setOtherMemberButton(false);
														handleUnlinkClosedMember();
													}}
												>
													<ListItemText>
														{t('button.unlinkClosedMember')}
													</ListItemText>
												</MenuItem>
											) : (
												''
											)}
										</MenuList>
									</Paper>
								</Grow>
							)}
						</Popper>
					</Stack>
				</Stack>

				{/* Action dialogs rendered at page level (outside the menus) so their
				    keystrokes no longer bubble into the MenuList type-ahead handler. */}
				{otherDialog === 'submitToIC' && (
					<SubmitToICDialog open onClose={() => setOtherDialog(null)} />
				)}
				{otherDialog === 'returnFromIC' && (
					<ReturnFromICDialog open onClose={() => setOtherDialog(null)} />
				)}
				{otherDialog === 'assignCaseWorkers' && (
					<AssignCaseWorkersDialog open onClose={() => setOtherDialog(null)} />
				)}
				{otherDialog === 'assignProgramWorkers' && (
					<AssignProgramWorkersDialog
						open
						onClose={() => setOtherDialog(null)}
					/>
				)}
				{otherDialog === 'closeNotSuitable' && (
					<CloseNotSuitableDialog open onClose={() => setOtherDialog(null)} />
				)}
				{otherDialog === 'transferCaseWorkers' && (
					<TransferCaseWorkersDialog
						open
						onClose={() => setOtherDialog(null)}
					/>
				)}
				{otherDialog === 'transferProgramWorkers' && (
					<TransferProgramWorkersDialog
						open
						onClose={() => setOtherDialog(null)}
					/>
				)}
				{otherDialog === 'updateSharedWithProfessionals' && (
					<UpdateSharedWithProfessionalsDialog
						open
						onClose={() => setOtherDialog(null)}
					/>
				)}
				{otherDialog === 'clearAllCaseWorkers' && (
					<ClearAllCaseWorkersDialog
						open
						details={details?.data}
						onClose={() => setOtherDialog(null)}
					/>
				)}
				{otherDialog === 'clearAllProgramWorkers' && (
					<ClearAllProgramWorkersDialog
						open
						details={details?.data}
						onClose={() => setOtherDialog(null)}
					/>
				)}
				<ClosedMemberPickerDialog
					open={otherDialog === 'linkClosedMember'}
					onClose={() => setOtherDialog(null)}
					onConfirm={async (row) => {
						const response = await linkClosedMember({
							userId: admissionRequestId || '',
							closedUserId: row.id || '',
						});
						if ('data' in response) {
							enqueueSnackbar(
								t('snackbar.linkClosedMemberSuccess', { fileNo: row.fileNo }),
								{
									variant: 'success',
									anchorOrigin: { horizontal: 'center', vertical: 'top' },
									autoHideDuration: 3000,
									action: (key) => (
										<CloseIcon onClick={() => closeSnackbar(key)} />
									),
								}
							);
							setOtherDialog(null);
						}
					}}
					onConfirmLegacy={async (legacyFileNo) => {
						const response = await linkLegacyFileNo({
							userId: admissionRequestId || '',
							legacyFileNo,
						});
						if ('data' in response) {
							enqueueSnackbar(
								t('snackbar.linkClosedMemberSuccess', { fileNo: legacyFileNo }),
								{
									variant: 'success',
									anchorOrigin: { horizontal: 'center', vertical: 'top' },
									autoHideDuration: 3000,
									action: (key) => (
										<CloseIcon onClick={() => closeSnackbar(key)} />
									),
								}
							);
							setOtherDialog(null);
						}
					}}
				/>

				{membershipDialog === 'openCase' && (
					<NewOpenCaseDialog
						open
						details={details?.data}
						onClose={() => setMembershipDialog(null)}
					/>
				)}
				{membershipDialog === 'closeCase' && (
					<NewCloseCaseDialog open onClose={() => setMembershipDialog(null)} />
				)}
				{membershipDialog === 'closeAllProgram' && (
					<NewCloseAllProgramDialog
						open
						onClose={() => setMembershipDialog(null)}
					/>
				)}
				{membershipDialog === 'closeAllCase' && (
					<NewCloseAllCaseDialog
						open
						onClose={() => setMembershipDialog(null)}
					/>
				)}

				{/* Duplicate-check dialogs stay mounted and run their own open/warning
				    state machine; the parent only bumps a per-key trigger counter. */}
				<NewProgramMemberDialog
					trigger={dupTrigger.newProgramMember || 0}
					details={details?.data}
				/>
				<NewCaseMemberDialog
					trigger={dupTrigger.newCaseMember || 0}
					details={details?.data}
				/>

				{details?.data.linkedFamilyMemberId && (
					<LinkedRecordBanner
						linkedTo='familyMember'
						fileNo={details.data.familyMemberFileNo}
						href={
							has(PermissionEnum.ViewFamilyMember)
								? `/family-management/family-management-list/detail/${details.data.linkedFamilyMemberId}`
								: undefined
						}
					/>
				)}

				<Box display='flex' justifyContent='space-between'>
					<Tabs
						value={activeTab}
						onChange={handleTabChange}
						// sx={{ marginBottom: 3 }}
					>
						<Tab
							label={
								<Typography variant='body1'>{t('member.details')}</Typography>
							}
							value='details'
							sx={{ textTransform: 'none' }}
						/>
						{canSeeTab.intervention && (
							<Tab
								label={
									<Typography variant='body1'>
										{t('member.intervention')}
									</Typography>
								}
								value='intervention'
								sx={{ textTransform: 'none' }}
							/>
						)}

						{canSeeTab.family && (
							<Tab
								label={
									<Typography variant='body1'>
										{t('member.manageFamilyMember')}
									</Typography>
								}
								value='family'
								sx={{ textTransform: 'none' }}
							/>
						)}
						{canSeeTab[PROGRAM_APP_LOGIN_TAB] && (
							<Tab
								label={
									<ProgramAppLoginTabLabel
										entity='member'
										id={admissionRequestId || ''}
									/>
								}
								value={PROGRAM_APP_LOGIN_TAB}
								sx={{ textTransform: 'none' }}
							/>
						)}
						{canSeeTab.history && (
							<Tab
								label={
									<Typography variant='body1'>{t('member.history')}</Typography>
								}
								value='history'
								sx={{ textTransform: 'none' }}
							/>
						)}
					</Tabs>
					{activeTab == 'intervention' &&
						has(PermissionEnum.CaseManagementCreateIntervention) &&
						![
							MemberStatusEnum.InProgressNoAdmitted,
							MemberStatusEnum.InProgressAdmitted,
							MemberStatusEnum.AwaitingAssignmentNoAdmitted,
							MemberStatusEnum.AwaitingAssignmentAdmitted,
						].includes(memberStatus as MemberStatusEnum) && (
							<Button
								endIcon={<EditCalendarOutlinedIcon />}
								onClick={() => {
									navigate(
										`/member-management/${
											type == 'member'
												? 'member-list'
												: 'admission-request-list'
										}/detail/${admissionRequestId}/create/${admissionRequestId}/${
											type == 'member' ? 'member-detail' : 'admission-detail'
										}`
									);
								}}
								variant='outlined'
								sx={{ marginRight: '20px' }}
							>
								{t('button.newIntervention')}
							</Button>
						)}
				</Box>

				{activeTab == 'details' && (
					<>
						<Accordion
							expanded={serviceDetailsExpanded}
							onChange={() =>
								setServiceDetailsExpanded(!serviceDetailsExpanded)
							}
							sx={{
								borderRadius: '16px!important',
								'&::before': {
									backgroundColor: 'transparent !important',
								},
								marginBottom: '24px',
								padding: '8px',
							}}
						>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										width: '100%',
									}}
								>
									<Typography
										sx={{
											fontWeight: 'bold',
											display: 'flex',
											alignItems: 'center',
										}}
									>
										{t('member.serviceDetails')}
									</Typography>

									<Box marginRight={2}>
										{/* <Button
				onClick={(e) => {
					e.stopPropagation();
					handleOpenUpload(type);
				}}
				sx={{ marginRight: '8px', textTransform: 'none' }}
				variant={type === FormManagementType.all ? 'contained' : 'outlined'}
			>
				{type === FormManagementType.all
					? t('button.uploadForm')
					: t('button.upload')}
			</Button> */}
									</Box>
								</Box>
							</AccordionSummary>
							<AccordionDetails>
								<Grid container spacing={3} marginTop={0}>
									<Grid size={6}>
										<AppFieldView
											label={t('member.fileNo')}
											value={details?.data.fileNo}
											editButton={
												<FileNoHistoryButton
													userId={details?.data.id}
													fileNo={details?.data.fileNo}
													hasFileNoHistory={details?.data.hasFileNoHistory}
												/>
											}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.preAdmissionNo')}
											value={details?.data.preAdmissionNo}
											link={`/pre-admission/pre-admission-list/detail/${details?.data.id}`}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.unit')}
											value={details?.data.branchName}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.admissionDate')}
											value={
												details?.data.admissionDate
													? dayjs(details?.data.admissionDate)
															.locale('en')
															.format(DISPLAY_DATE_FORMAT)
													: ''
											}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.createdBy')}
											value={details?.data.createdBy}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.lastModified')}
											value={
												details?.data.updatedAt
													? dayjs(details?.data.updatedAt)
															.locale('en')
															.format(DISPLAY_DATE_TIME_FORMAT)
													: ''
											}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.status')}
											value={details?.data.statusDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.actionRequiredby')}
											value={details?.data.actionRequiredBy}
										/>
									</Grid>
								</Grid>
							</AccordionDetails>
						</Accordion>

						<Accordion
							expanded={personalInformationExpanded}
							onChange={() =>
								setPersonalInformationExpanded(!personalInformationExpanded)
							}
							sx={{
								borderRadius: '16px!important',
								'&::before': {
									backgroundColor: 'transparent !important',
								},
								marginBottom: '24px',
								padding: '8px',
							}}
						>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										width: '100%',
									}}
								>
									<Typography
										sx={{
											fontWeight: 'bold',
											display: 'flex',
											alignItems: 'center',
										}}
									>
										{t('member.personalInformation')}
									</Typography>

									<Box marginRight={2}>
										{userInfo?.permissionList?.includes(
											PermissionEnum.EditAdmissionRequestAndMember
										) && (
											<Button
												onClick={(e) => {
													e.stopPropagation();
													navigate(
														`/member-management/${
															type == 'member'
																? 'member-list'
																: 'admission-request-list'
														}/detail/${admissionRequestId}/personal`
													);
												}}
												sx={{ marginRight: '8px', textTransform: 'none' }}
												variant='outlined'
											>
												{t('button.edit')}
											</Button>
										)}
									</Box>
								</Box>
							</AccordionSummary>
							<AccordionDetails>
								<Grid container spacing={3} marginTop={0}>
									<Grid size={6}>
										<AppFieldView
											label={t('member.name_en')}
											value={details?.data.nameEn}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.name_zh')}
											value={details?.data.nameZh}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.dateOfBirth')}
											value={
												details?.data.dateOfBirth
													? dayjs(details?.data.dateOfBirth)
															.locale('en')
															.format(DISPLAY_DATE_FORMAT)
													: ''
											}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.currentAge')}
											labelElement={
												details?.data.currentAgeEstimated && (
													<EstimatedAgeInfoHint
														currentAge={details?.data.currentAge}
													/>
												)
											}
											value={formatAge(
												details?.data.currentAge,
												details?.data.currentAgeEstimated
											)}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.sex')}
											value={details?.data.sexDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.HKID')}
											value={details?.data.hkid}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.dialect')}
											value={details?.data.dialectDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.maritalStatus')}
											value={details?.data.maritalStatusDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.placeOfBirth')}
											value={details?.data.placeOfBirthDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.placeOfBirthSpecify')}
											value={details?.data.placeOfBirthSpecify}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.yearArrivingHK')}
											value={details?.data.yearOfArrivingHk}
										/>
									</Grid>
								</Grid>
							</AccordionDetails>
						</Accordion>

						<Accordion
							expanded={contactInformationExpanded}
							onChange={() =>
								setContactInformationExpanded(!contactInformationExpanded)
							}
							sx={{
								borderRadius: '16px!important',
								'&::before': {
									backgroundColor: 'transparent !important',
								},
								marginBottom: '24px',
								padding: '8px',
							}}
						>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										width: '100%',
									}}
								>
									<Typography
										sx={{
											fontWeight: 'bold',
											display: 'flex',
											alignItems: 'center',
										}}
									>
										{t('member.contactInformation')}
									</Typography>

									<Box marginRight={2}>
										{userInfo?.permissionList?.includes(
											PermissionEnum.EditAdmissionRequestAndMember
										) && (
											<Button
												onClick={(e) => {
													e.stopPropagation();
													navigate(
														`/member-management/${
															type == 'member'
																? 'member-list'
																: 'admission-request-list'
														}/detail/${admissionRequestId}/contact`
													);
												}}
												sx={{ marginRight: '8px', textTransform: 'none' }}
												variant='outlined'
											>
												{t('button.edit')}
											</Button>
										)}
									</Box>
								</Box>
							</AccordionSummary>
							<AccordionDetails>
								<Grid container spacing={3} marginTop={0}>
									<Grid size={6}>
										<AppFieldView
											label={t('member.mobilePhone')}
											value={details?.data.phone}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.homeTel')}
											value={details?.data.homeTel}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.otherPhone')}
											value={details?.data.otherPhone}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.email')}
											value={details?.data.email}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.typeOfHousing')}
											value={details?.data.typeOfHousingDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.typeOfHousingSpecify')}
											value={details?.data.typeOfHousingSpecify}
										/>
									</Grid>
									<Grid size={12}>
										<AppFieldView
											label={t('member.addressLanguage')}
											value={showLabel(
												details?.data.addressLanguage,
												LANGUAGE_LIST
											)}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.region')}
											value={details?.data.regionDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.district')}
											value={details?.data.districtDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.subdistrict')}
											value={details?.data.subdistrictDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.estateCourtVillage')}
											value={details?.data.estateCourtVillageDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.roadStreet')}
											value={details?.data.roadStreet}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.roadStreetNo')}
											value={details?.data.roadStreetNo}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.buildingBlock')}
											value={details?.data.buildingBlockDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.buildingBlockSpecify')}
											value={details?.data.buildingBlockSpecify}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.floor')}
											value={details?.data.floor}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.flat')}
											value={details?.data.flat}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.fullAddress')}
											value={details?.data.fullAddress}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.useFullAddressAsMailAddress')}
											value={
												details?.data.useFullAddressAsMailAddress ? 'Yes' : 'No'
											}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.mailAddress')}
											value={details?.data.mailAddress}
										/>
									</Grid>
								</Grid>
							</AccordionDetails>
						</Accordion>

						<Accordion
							expanded={familyCarersInformationExpanded}
							onChange={() =>
								setFamilyCarersInformationExpanded(
									!familyCarersInformationExpanded
								)
							}
							sx={{
								borderRadius: '16px!important',
								'&::before': {
									backgroundColor: 'transparent !important',
								},
								marginBottom: '24px',
								padding: '8px',
							}}
						>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										width: '100%',
									}}
								>
									<Typography
										sx={{
											fontWeight: 'bold',
											display: 'flex',
											alignItems: 'center',
										}}
									>
										{t('preAdmission.familyCarersInformation')}
										<FamilyCarersInfoHint />
									</Typography>

									<Box marginRight={2}>
										{userInfo?.permissionList?.includes(
											PermissionEnum.EditAdmissionRequestAndMember
										) && (
											<Button
												onClick={(e) => {
													e.stopPropagation();
													navigate(
														`/member-management/${
															type == 'member'
																? 'member-list'
																: 'admission-request-list'
														}/detail/${admissionRequestId}/family`
													);
												}}
												sx={{ marginRight: '8px', textTransform: 'none' }}
												variant='outlined'
											>
												{t('button.edit')}
											</Button>
										)}
									</Box>
								</Box>
							</AccordionSummary>
							<AccordionDetails>
								<Grid container spacing={3} marginTop={0}>
									<Grid size={12}>
										<LocalDataGrid
											rows={details?.data.familyCarersList || []}
											columns={columns}
											getRowId={(row) => row.id}
											pagination={false}
										/>
									</Grid>
								</Grid>
							</AccordionDetails>
						</Accordion>

						<Accordion
							expanded={educationWorkLiveExpanded}
							onChange={() =>
								setEducationWorkLiveExpanded(!educationWorkLiveExpanded)
							}
							sx={{
								borderRadius: '16px!important',
								'&::before': {
									backgroundColor: 'transparent !important',
								},
								marginBottom: '24px',
								padding: '8px',
							}}
						>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										width: '100%',
									}}
								>
									<Typography
										sx={{
											fontWeight: 'bold',
											display: 'flex',
											alignItems: 'center',
										}}
									>
										{t('member.educationWorkLive')}
									</Typography>

									<Box marginRight={2}>
										{userInfo?.permissionList?.includes(
											PermissionEnum.EditAdmissionRequestAndMember
										) && (
											<Button
												onClick={(e) => {
													e.stopPropagation();
													navigate(
														`/member-management/${
															type == 'member'
																? 'member-list'
																: 'admission-request-list'
														}/detail/${admissionRequestId}/work`
													);
												}}
												sx={{ marginRight: '8px', textTransform: 'none' }}
												variant='outlined'
											>
												{t('button.edit')}
											</Button>
										)}
									</Box>
								</Box>
							</AccordionSummary>
							<AccordionDetails>
								<Grid container spacing={3} marginTop={0}>
									<Grid size={6}>
										<AppFieldView
											label={t('member.financialCondition')}
											value={details?.data.financialConditionDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.workStatus')}
											value={details?.data.workStatusDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.workMotivation')}
											value={details?.data.workMotivationDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.education')}
											value={details?.data.educationDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.studyingSecondarySchool')}
											value={
												details?.data.studyingSecondarySchool == null
													? ''
													: details.data.studyingSecondarySchool
														? 'Yes'
														: 'No'
											}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.studyingSecondarySchoolSpecify')}
											value={details?.data.chiFullNameOfSecondarySchool}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.attendingOtherYouthService')}
											value={
												details?.data.attendingOtherYouthService == null
													? ''
													: details.data.attendingOtherYouthService
														? 'Yes'
														: 'No'
											}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.nameOtherYouthService')}
											value={details?.data.nameOfOtherYouthService}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.livingStatus')}
											value={details?.data.livingStatusDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.nameResidentialCareHome')}
											value={details?.data.nameOfResidentialCareHome}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.livingWithPersonAgedUnder18')}
											value={details?.data.livingWithPersonsAgedUnder18Desc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.peopleLivingTogether')}
											value={details?.data.noOfPeopleLivingTogether}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.degreeFamilySupport')}
											value={details?.data.degreeOfFamilySupportDesc}
										/>
									</Grid>
								</Grid>
							</AccordionDetails>
						</Accordion>

						<Accordion
							expanded={memberInformationExpanded}
							onChange={() =>
								setMemberInformationExpanded(!memberInformationExpanded)
							}
							sx={{
								borderRadius: '16px!important',
								'&::before': {
									backgroundColor: 'transparent !important',
								},
								marginBottom: '24px',
								padding: '8px',
							}}
						>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										width: '100%',
									}}
								>
									<Typography
										sx={{
											fontWeight: 'bold',
											display: 'flex',
											alignItems: 'center',
										}}
									>
										{t('preAdmission.membershipInformation')}
									</Typography>

									<Box marginRight={2}>
										{userInfo?.permissionList?.includes(
											PermissionEnum.EditAdmissionRequestAndMember
										) && (
											<Button
												onClick={(e) => {
													e.stopPropagation();
													navigate(
														`/member-management/${
															type == 'member'
																? 'member-list'
																: 'admission-request-list'
														}/detail/${admissionRequestId}/membership`
													);
												}}
												sx={{ marginRight: '8px', textTransform: 'none' }}
												variant='outlined'
											>
												{t('button.edit')}
											</Button>
										)}
									</Box>
								</Box>
							</AccordionSummary>
							<AccordionDetails>
								<Grid container spacing={3} marginTop={0}>
									<Grid size={6}>
										<AppFieldView
											label={t('member.membershipRole')}
											value={details?.data.membershipRole}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.membershipState')}
											value={details?.data.membershipStateDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.dateMembershipStart')}
											value={
												details?.data.dateOfTwcMembershipStart
													? dayjs(details?.data.dateOfTwcMembershipStart)
															.locale('en')
															.format(DISPLAY_DATE_FORMAT)
													: ''
											}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.dateMembershipRenewal')}
											value={
												details?.data.dateOfMembershipRenewal
													? dayjs(details?.data.dateOfMembershipRenewal)
															.locale('en')
															.format(DISPLAY_DATE_FORMAT)
													: ''
											}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.renewalReason')}
											value={details?.data.renewalReasonDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.renewalReasonSpecify')}
											value={details?.data.renewalReasonSpecify}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.intakeAge')}
											value={formatAge(
												details?.data.intakeAge,
												details?.data.intakeAgeEstimated
											)}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.followYear')}
											value={details?.data.followYear}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.mailRequest')}
											value={showLabel(details?.data.mailRequest, YESNOLIST)}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.newsletterConfidential')}
											value={showLabel(
												details?.data.newsletterConfidential,
												YESNOLIST
											)}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.contactMethod')}
											value={details?.data.contactMethodDesc || ''}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.homeVisit')}
											value={details?.data.homeVisitDesc || ''}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.inactiveMember')}
											value={showLabel(details?.data.inactiveMember, YESNOLIST)}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.dateExitService')}
											value={
												details?.data.dateOfExitService
													? dayjs(details?.data.dateOfExitService)
															.locale('en')
															.format(DISPLAY_DATE_FORMAT)
													: ''
											}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.notSuitableReason')}
											value={details?.data.notSuitableReasonDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.notSuitableReasonSpecify')}
											value={details?.data.notSuitableReasonSpecify}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.responsibleProgramWorker')}
											value={details?.data.responsibleProgramWorkerName}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.responsibleProfessionalStaff')}
											value={details?.data.responsibleProfessionalStaffName}
										/>
									</Grid>
								</Grid>
							</AccordionDetails>
						</Accordion>

						<Accordion
							expanded={caseStatusExpanded}
							onChange={() => setCaseStatusExpanded(!caseStatusExpanded)}
							sx={{
								borderRadius: '16px!important',
								'&::before': {
									backgroundColor: 'transparent !important',
								},
								marginBottom: '24px',
								padding: '8px',
							}}
						>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										width: '100%',
									}}
								>
									<Typography
										sx={{
											fontWeight: 'bold',
											display: 'flex',
											alignItems: 'center',
										}}
									>
										{t('preAdmission.caseStatus')}
									</Typography>

									<Box marginRight={2}>
										{userInfo?.permissionList?.includes(
											PermissionEnum.EditAdmissionRequestAndMember
										) && (
											<Button
												onClick={(e) => {
													e.stopPropagation();
													navigate(
														`/member-management/${
															type == 'member'
																? 'member-list'
																: 'admission-request-list'
														}/detail/${admissionRequestId}/case`
													);
												}}
												sx={{ marginRight: '8px', textTransform: 'none' }}
												variant='outlined'
											>
												{t('button.edit')}
											</Button>
										)}
									</Box>
								</Box>
							</AccordionSummary>
							<AccordionDetails>
								<Grid container spacing={3} marginTop={0}>
									<Grid size={6}>
										<AppFieldView
											label={t('member.isCase')}
											value={showLabel(details?.data.isCase, ISCASE_LIST)}
										/>
									</Grid>
									<Grid size={6}></Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.dateReceivingReferralApplication')}
											value={
												details?.data.receivingReferralOrApplicationDate
													? dayjs(
															details?.data.receivingReferralOrApplicationDate
														)
															.locale('en')
															.format(DISPLAY_DATE_FORMAT)
													: ''
											}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.dateCaseOpen')}
											value={
												details?.data.dateOfCaseOpen
													? dayjs(details?.data.dateOfCaseOpen)
															.locale('en')
															.format(DISPLAY_DATE_FORMAT)
													: ''
											}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.dateConsentObtained')}
											value={
												details?.data.dateOfConsentObtained
													? dayjs(details?.data.dateOfConsentObtained)
															.locale('en')
															.format(DISPLAY_DATE_FORMAT)
													: ''
											}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.serviceFollow')}
											value={details?.data.serviceFollowDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.responsibleCaseWorker')}
											value={details?.data.responsibleCaseWorkerName}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.relievingWorker')}
											value={details?.data.relievingWorkerName}
										/>
									</Grid>
									<Grid size={12}>
										<AppFieldView
											label={t('member.sharedWithProfessionals')}
											value={details?.data.sharedWithProfessionalsDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.lastMeetingInterventionDate')}
											value={
												details?.data.lastMeetingInterventionDate
													? dayjs(details?.data.lastMeetingInterventionDate)
															.locale('en')
															.format(DISPLAY_DATE_FORMAT)
													: ''
											}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.lastMeetingInterventionAlert')}
											value={details?.data.lastMeetingInterventionAlert}
										/>
									</Grid>
									<Grid size={12}>
										<AppFieldView
											label={t('member.dateCaseClosed')}
											value={
												details?.data.dateOfCaseClosed
													? dayjs(details?.data.dateOfCaseClosed)
															.locale('en')
															.format(DISPLAY_DATE_FORMAT)
													: ''
											}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.closedReason')}
											value={details?.data.closedReasonDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.closedReasonSpecify')}
											value={details?.data.closedReasonSpecify}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.completeServiceExit')}
											value={showLabel(
												details?.data.completeServiceExit,
												YESNOLIST
											)}
										/>
									</Grid>
								</Grid>
							</AccordionDetails>
						</Accordion>

						<Accordion
							expanded={referrerInformationExpanded}
							onChange={() =>
								setReferrerInformationExpanded(!referrerInformationExpanded)
							}
							sx={{
								borderRadius: '16px!important',
								'&::before': {
									backgroundColor: 'transparent !important',
								},
								marginBottom: '24px',
								padding: '8px',
							}}
						>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										width: '100%',
									}}
								>
									<Typography
										sx={{
											fontWeight: 'bold',
											display: 'flex',
											alignItems: 'center',
										}}
									>
										{t('member.referrerInformation')}
									</Typography>

									<Box marginRight={2}>
										{userInfo?.permissionList?.includes(
											PermissionEnum.EditAdmissionRequestAndMember
										) && (
											<Button
												onClick={(e) => {
													e.stopPropagation();
													navigate(
														`/member-management/${
															type == 'member'
																? 'member-list'
																: 'admission-request-list'
														}/detail/${admissionRequestId}/referrer`
													);
												}}
												sx={{ marginRight: '8px', textTransform: 'none' }}
												variant='outlined'
											>
												{t('button.edit')}
											</Button>
										)}
									</Box>
								</Box>
							</AccordionSummary>
							<AccordionDetails>
								<Grid container spacing={3} marginTop={0}>
									<Grid size={12}>
										<AppFieldView
											label={t('member.approachSource')}
											value={details?.data.approachSourceDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.profileOfReferrals')}
											value={details?.data.profileOfReferralDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.profileOfReferrals(specify)')}
											value={details?.data.profileOfReferralSpecify}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.referralAgency')}
											value={details?.data.referralAgencyDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.referralAgency(specify)')}
											value={details?.data.referralAgencySpecify}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.prefix')}
											value={showLabel(
												details?.data.prefixOfReferrer,
												PREFIX_LIST
											)}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.nameOfReferrer')}
											value={details?.data.nameOfReferrer}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.positionOfReferrer')}
											value={details?.data.positionOfReferrer}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.referrerContactNo')}
											value={details?.data.referrerContactNo}
										/>
									</Grid>
									<Grid size={12}>
										<AppFieldView
											label={t('preAdmission.referrerFaxNo')}
											value={details?.data.referrerFaxNo}
										/>
									</Grid>
								</Grid>
							</AccordionDetails>
						</Accordion>

						<Accordion
							expanded={shareCaseServiceExpanded}
							onChange={() =>
								setShareCaseServiceExpanded(!shareCaseServiceExpanded)
							}
							sx={{
								borderRadius: '16px!important',
								'&::before': {
									backgroundColor: 'transparent !important',
								},
								marginBottom: '24px',
								padding: '8px',
							}}
						>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										width: '100%',
									}}
								>
									<Typography
										sx={{
											fontWeight: 'bold',
											display: 'flex',
											alignItems: 'center',
										}}
									>
										{t('preAdmission.shareCaseService')}
									</Typography>

									<Box marginRight={2}>
										{userInfo?.permissionList?.includes(
											PermissionEnum.EditAdmissionRequestAndMember
										) && (
											<Button
												onClick={(e) => {
													e.stopPropagation();
													navigate(
														`/member-management/${
															type == 'member'
																? 'member-list'
																: 'admission-request-list'
														}/detail/${admissionRequestId}/share`
													);
												}}
												sx={{ marginRight: '8px', textTransform: 'none' }}
												variant='outlined'
											>
												{t('button.edit')}
											</Button>
										)}
									</Box>
								</Box>
							</AccordionSummary>
							<AccordionDetails>
								<Grid container spacing={3} marginTop={0}>
									<Grid size={12}>
										<AppFieldView
											label={t('preAdmission.otherOrganisationServingSameCase')}
											value={showLabel(
												details?.data.otherOrganisationServing,
												YESNOLIST
											)}
										/>
									</Grid>
									<Grid size={12}>
										<LocalDataGrid
											rows={details?.data.organisationList || []}
											columns={columns1}
											getRowId={(row) => row.id}
											pagination={false}
										/>
									</Grid>
								</Grid>
							</AccordionDetails>
						</Accordion>

						<Accordion
							expanded={mentalHealthInformationExpanded}
							onChange={() =>
								setMentalHealthInformationExpanded(
									!mentalHealthInformationExpanded
								)
							}
							sx={{
								borderRadius: '16px!important',
								'&::before': {
									backgroundColor: 'transparent !important',
								},
								marginBottom: '24px',
								padding: '8px',
							}}
						>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										width: '100%',
									}}
								>
									<Typography
										sx={{
											fontWeight: 'bold',
											display: 'flex',
											alignItems: 'center',
										}}
									>
										{t('preAdmission.mentalHealthInformation')}
									</Typography>

									<Box marginRight={2}>
										{userInfo?.permissionList?.includes(
											PermissionEnum.EditAdmissionRequestAndMember
										) && (
											<Button
												onClick={(e) => {
													e.stopPropagation();
													navigate(
														`/member-management/${
															type == 'member'
																? 'member-list'
																: 'admission-request-list'
														}/detail/${admissionRequestId}/health`
													);
												}}
												sx={{ marginRight: '8px', textTransform: 'none' }}
												variant='outlined'
											>
												{t('button.edit')}
											</Button>
										)}
									</Box>
								</Box>
							</AccordionSummary>
							<AccordionDetails>
								<Grid container spacing={3} marginTop={0}>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.followUpClinicPsychiatry')}
											value={details?.data.followUpClinicPsychiatryDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.mentalHealthConditionOther')}
											value={details?.data.mentalHealthConditionDesc}
										/>
									</Grid>

									<Grid size={12}>
										<AppFieldView
											label={t('member.diagnosisStatus')}
											value={details?.data.diagnosisStatusDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.mainDiagnosis')}
											value={details?.data.mainDiagnosisDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.mainDiagnosisSpecify')}
											value={details?.data.mainDiagnosisSpecify}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.secondaryDiagnosis')}
											value={details?.data.secondaryDiagnosisDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.secondaryDiagnosisSpecify')}
											value={details?.data.secondaryDiagnosisSpecify}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.suspectedMentalHealthProblem')}
											value={details?.data.suspectedMentalHealthProblemDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t(
												'preAdmission.suspectedMentalHealthProblemSpecify'
											)}
											value={details?.data.suspectedMentalHealthProblemSpecify}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.initialRiskLevel')}
											value={details?.data.initialRiskLevelDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.currentRiskLevel')}
											value={details?.data.currentRiskLevelDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.suicideIdeation')}
											value={details?.data.suicideIdeationDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.suicideHistory')}
											value={details?.data.suicideHistoryDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.violenceIdeation')}
											value={details?.data.violenceIdeationDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.violenceHistory')}
											value={details?.data.violenceHistoryDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.abuseHistory')}
											value={details?.data.abuseHistoryDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.abuseRole')}
											value={details?.data.abuseRoleDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.abuseType')}
											value={details?.data.abuseTypeDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.abuseTypeSpecify')}
											value={details?.data.abuseTypeSpecify}
										/>
									</Grid>
									<Grid size={12}>
										<AppFieldView
											label={t('preAdmission.addictionProblem')}
											value={showLabel(
												details?.data.addictionProblem,
												(allEnumList?.data.AddictionProblemEnum || []).map(
													(item: any) => {
														return {
															...item,
															id: item.code,
															name: item.desc,
														};
													}
												)
											)}
										/>
									</Grid>

									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.typeAddictionProblem')}
											value={details?.data.typeOfAddictionProblemDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.typeAddictionProblemSpecify')}
											value={details?.data.typeOfAddictionProblemSpecify}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.coreConcern')}
											value={details?.data.coreConcernDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.dateLinkUpHealthSystem')}
											value={
												details?.data.dateOfLinkUpToHealthSystem
													? dayjs(details?.data.dateOfLinkUpToHealthSystem)
															.locale('en')
															.format(DISPLAY_DATE_FORMAT)
													: ''
											}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.familyMembersHaveHistoryMI')}
											value={
												details?.data
													.familyMembersHaveHistoryOfMentalIllnessDesc
											}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.OtherIllnessDisability')}
											value={details?.data.otherIllnessDisability}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.remarks(EnquiryRecord)')}
											value={details?.data.enquiryRecordRemarks}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.remarks(MembershipApplication)')}
											value={details?.data.membershipApplicationRemarks}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.remarks(FaceSheet)')}
											value={details?.data.faceSheetRemarks}
										/>
									</Grid>
								</Grid>
							</AccordionDetails>
						</Accordion>
					</>
				)}

				{activeTab == 'intervention' && <MemberInterventionSummaryPage />}

				{activeTab == 'family' && <FamilyPage details={details} />}

				{activeTab == PROGRAM_APP_LOGIN_TAB && (
					<ProgramAppLoginTab
						entity='member'
						id={admissionRequestId || ''}
						hasHkid={!!details?.data.hkid}
						hasPhone={!!details?.data.phone}
						canEdit={has(PermissionEnum.EditAdmissionRequestAndMember)}
						linkedRecord={
							details?.data.linkedFamilyMemberId
								? {
										linkedTo: 'familyMember',
										fileNo: details.data.familyMemberFileNo,
										href: has(PermissionEnum.ViewFamilyMember)
											? `/family-management/family-management-list/detail/${details.data.linkedFamilyMemberId}`
											: undefined,
									}
								: undefined
						}
					/>
				)}

				{activeTab == 'history' && <HistorySummaryPage />}
			</Box>
		</>
	);
}
